import faker from 'faker'
import { type ApolloClient, useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { type PipelineStep } from '../types/attributeManagement.types'
import {
  GetStepVersionDocument,
  type GetStepVersionQuery,
  type GetStepVersionQueryVariables,
  type Instacart_Catalog_Catflow_V1_StepVersion,
} from '.'

export interface GetStepVersionsResponse {
  data: { [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion }
  isLoading: boolean
  error: unknown
}

export const fetchStepVersion = (
  client: ApolloClient<unknown>,
  variables: GetStepVersionQueryVariables
) =>
  client.query<GetStepVersionQuery, GetStepVersionQueryVariables>({
    query: GetStepVersionDocument,
    variables: variables,
  })

export const useGetBulkStepVersionQuery = (steps: PipelineStep[]) => {
  const client = useApolloClient()
  const [responses, setResponses] = useState<{
    [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion
  } | null>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<unknown>(null)

  const fetchStepVersions = useCallback(
    async (steps: PipelineStep[]) => {
      try {
        setResponses(null)
        setIsLoading(true)

        const queries = steps.map(step => {
          if (!step?.stepId || !step?.version) return null
          const idempotencyKey = faker.random.uuid()
          const idemKey = idempotencyKey + step?.stepId + step?.version
          const result = fetchStepVersion(client, {
            stepId: step.stepId || '',
            version: step.version || '',
            clientId: 'catalog_camp_ipp',
            idempotencyKey: idemKey,
          })
          return result
        })

        const results = await Promise.all(queries)

        // Create a map of stepId to payloads
        const stepIdToVersionMap: {
          [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion
        } = {}
        for (const result of results) {
          if (!result) continue
          const stepId =
            result.data.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion?.stepVersion?.stepId
          const response =
            result.data.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion?.stepVersion
          if (stepId === undefined || response === undefined) continue
          stepIdToVersionMap[stepId] = response
        }

        setResponses(stepIdToVersionMap)
      } catch (e) {
        setError(e)
      } finally {
        setIsLoading(false)
      }
    },
    [client]
  )

  useEffect(() => {
    fetchStepVersions(steps)
  }, [fetchStepVersions, steps])

  const output: GetStepVersionsResponse = {
    data: responses ?? {},
    isLoading,
    error,
  }
  return output
}
